import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { adminApi, type Invitation, type User, type UserStatus } from "../../api/admin";
import type { ApiError } from "../../api/client";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import { Pagination } from "../../components/Pagination";
import { InviteUserForm } from "../../components/InviteUserForm";
import { useAuth } from "../../auth/AuthContext";
import { formatApiError } from "../../lib/apiError";
import { useScrollLock } from "../../lib/useScrollLock";

const PAGE_SIZE = 25;

export default function AdminUsersPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  // Sprint 7: one function per action — no "manage".
  const canEdit = hasPermission("users:edit");
  const canDelete = hasPermission("users:delete");
  const canInvite = hasPermission("users:invite");
  const [items, setItems] = useState<User[] | null>(null);
  const [pending, setPending] = useState<Invitation[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<User | null>(null);
  const [inviting, setInviting] = useState(false);

  async function load() {
    setError(null);
    try {
      const result = await adminApi.listUsers(page, PAGE_SIZE);
      setItems(result.content.filter(Boolean));
      setTotalPages(result.totalPages);
    } catch (err) {
      setError((err as ApiError).message);
    }
  }

  async function loadPending() {
    try {
      setPending(await adminApi.listPendingInvitations());
    } catch {
      setPending([]);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (canInvite) void loadPending();
  }, [canInvite]);

  async function handleResend(inv: Invitation) {
    try {
      await adminApi.resendInvitation(inv.id);
      toast.success(t("admin.users.toast.resent", "Pozivnica ponovo poslata na {{email}}.", { email: inv.email }));
      await loadPending();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function handleRevoke(inv: Invitation) {
    if (!(await confirm({ message: t("admin.users.confirmRevoke", "Opozvati pozivnicu na čekanju za {{email}}?", { email: inv.email }), danger: true }))) return;
    try {
      await adminApi.revokeInvitation(inv.id);
      toast.success(t("admin.users.toast.revoked", "Pozivnica opozvana."));
      await loadPending();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function handleDelete(u: User) {
    if (!(await confirm({ message: t("admin.users.confirmDelete", "Obrisati korisnika {{email}}?", { email: u.email }), danger: true }))) return;
    try {
      await adminApi.deleteUser(u.id);
      toast.success(t("admin.users.toast.deleted", "Korisnik obrisan."));
      await load();
    } catch (err) {
      const e = err as ApiError;
      if (e.code === "LAST_ADMIN") toast.error(t("userAccess.error.lastAdmin", "Bar jedan aktivan korisnik mora zadržati dodelu pristupa i izmenu uloga."));
      else toast.error(formatApiError(e));
    }
  }

  async function toggle(u: User) {
    try {
      if (u.status === "ACTIVE") await adminApi.disableUser(u.id);
      else await adminApi.enableUser(u.id);
      await load();
      toast.success(u.status === "ACTIVE" ? t("admin.users.toast.disabled", "Korisnik onemogućen.") : t("admin.users.toast.enabled", "Korisnik omogućen."));
    } catch (err) {
      const e = err as ApiError;
      if (e.code === "LAST_ADMIN") toast.error(t("userAccess.error.lastAdmin", "Bar jedan aktivan korisnik mora zadržati dodelu pristupa i izmenu uloga."));
      else toast.error(formatApiError(e));
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-primary)" }}>{t("admin.users.title", "Korisnici")}</h1>
        {canInvite && (
          <button type="button" onClick={() => setInviting(true)} style={primary}>{t("admin.users.inviteUser", "+ Pozovi korisnika")}</button>
        )}
      </header>
      {error && <div style={errorBox}>{error}</div>}

      {pending.length > 0 && (
        <section style={panelStyle}>
          <h2 style={panelTitleStyle}>{t("admin.users.pendingTitle", "Pozivnice na čekanju ({{count}})", { count: pending.length })}</h2>
          <div className="tbl-wrap">
            <table className="list-card" style={table}>
              <thead>
                <tr>
                  <th style={th}>{t("admin.users.col.email", "Email")}</th>
                  <th style={th}>{t("admin.users.col.roles", "Uloge")}</th>
                  <th style={th}>{t("admin.users.col.expires", "Ističe")}</th>
                  <th style={{ ...th, textAlign: "right" }}>{t("admin.users.col.actions", "Radnje")}</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((p) => (
                  <tr key={p.id}>
                    <td style={td} data-label={t("admin.users.col.email", "Email")}>{p.email}</td>
                    <td style={td} data-label={t("admin.users.col.roles", "Uloge")}>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {p.roles.map((r) => (
                          <span key={r.id} style={chip}>{r.name}</span>
                        ))}
                      </div>
                    </td>
                    <td style={td} data-label={t("admin.users.col.expires", "Ističe")}>{new Date(p.expiresAt).toLocaleDateString("sr-Latn-RS")}</td>
                    <td className="card-actions" style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" onClick={() => handleResend(p)} style={linkBtn}>{t("admin.users.action.resend", "Pošalji ponovo")}</button>
                      <button type="button" onClick={() => handleRevoke(p)} style={dangerLink}>{t("admin.users.action.revoke", "Opozovi")}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!items ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.users.loading", "Učitavanje…")}</p>
      ) : items.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.users.empty", "Još nema korisnika.")}</p>
      ) : (
        <div className="tbl-wrap">
          <table className="list-card" style={table}>
            <thead>
              <tr>
                <th style={th}>{t("admin.users.col.email", "Email")}</th>
                <th style={th}>{t("admin.users.col.name", "Ime")}</th>
                <th style={th}>{t("admin.users.col.roles", "Uloge")}</th>
                <th style={th}>{t("admin.users.col.status", "Status")}</th>
                <th style={{ ...th, textAlign: "right" }}>{t("admin.users.col.actions", "Radnje")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id}>
                  <td style={td} data-label={t("admin.users.col.email", "Email")}>{u.email}</td>
                  <td style={td} data-label={t("admin.users.col.name", "Ime")}>{u.firstName} {u.lastName}</td>
                  <td style={td} data-label={t("admin.users.col.roles", "Uloge")}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {u.roles.map((r) => (
                        <span key={r.id} style={chip}>{r.name}</span>
                      ))}
                      {u.roles.length === 0 && <span style={{ color: "var(--ink-3)" }}>—</span>}
                      {u.hasOverrides && (
                        <span className="tag warn" title={t("admin.users.overridesHint", "Korisnik ima pojedinačno dodate ili oduzete funkcije.")}>
                          {t("admin.users.overrides", "+izuzeci")}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={td} data-label={t("admin.users.col.status", "Status")}><StatusBadge status={u.status} /></td>
                  <td className="card-actions" style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                    {/* users:view opens the picture read-only; users:access (which implies it) edits it. */}
                    <button type="button" onClick={() => navigate(`/admin/korisnici/${u.id}/pristup`)} style={linkBtn}>{t("admin.users.action.access", "Pristup")}</button>
                    {canEdit && (
                      <>
                        <button type="button" onClick={() => setEditing(u)} style={linkBtn}>{t("admin.users.action.edit", "Izmeni")}</button>
                        <button type="button" onClick={() => toggle(u)} style={linkBtn}>
                          {u.status === "ACTIVE" ? t("admin.users.action.disable", "Onemogući") : t("admin.users.action.enable", "Omogući")}
                        </button>
                      </>
                    )}
                    {canDelete && (
                      <button type="button" onClick={() => handleDelete(u)} style={dangerLink}>{t("action.delete", "Obriši")}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      {editing && (
        <UserEditModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      {inviting && (
        <div onClick={() => setInviting(false)} className="modal-overlay" style={overlay}>
          <div onClick={(e) => e.stopPropagation()} className="modal-panel" style={dialog}>
            <h2 style={{ margin: 0, fontSize: 18, color: "var(--brand-primary)" }}>{t("admin.users.inviteModalTitle", "Pozovi korisnika")}</h2>
            <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>
              {t("admin.users.inviteModalHint", "Primalac dobija imejl sa linkom koji važi 7 dana za postavljanje lozinke i dovršavanje registracije.")}
            </p>
            <InviteUserForm
              onCancel={() => setInviting(false)}
              onCreated={(inv) => {
                setInviting(false);
                toast.success(t("admin.users.toast.sent", "Pozivnica poslata na {{email}}.", { email: inv.email }));
                void loadPending();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function UserEditModal({ user, onClose, onSaved }: { user: User; onClose: () => void; onSaved: () => Promise<void> }) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useScrollLock();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await adminApi.updateUser(user.id, { firstName, lastName });
      await onSaved();
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div onClick={onClose} className="modal-overlay" style={overlay}>
      <div onClick={(e) => e.stopPropagation()} className="modal-panel" style={dialog}>
        <h2 style={{ margin: 0, fontSize: 18, color: "var(--brand-primary)" }}>{t("admin.users.modal.editTitle", "Izmena korisnika")}</h2>
        <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>{user.email}</p>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="form-row-wrap" style={{ display: "flex", gap: 12 }}>
            <Field label={t("admin.users.field.firstName", "Ime")} style={{ flex: 1 }}>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} style={input} />
            </Field>
            <Field label={t("admin.users.field.lastName", "Prezime")} style={{ flex: 1 }}>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} style={input} />
            </Field>
          </div>
          <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 12.5 }}>
            {t("admin.users.modal.accessHint", "Uloge i funkcije se menjaju na ekranu „Pristup“.")}
          </p>
          {error && <div style={errorBox}>{error}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={secondary}>{t("admin.users.action.cancel", "Otkaži")}</button>
            <button type="submit" disabled={submitting} style={primary}>
              {submitting ? t("admin.users.action.saving", "Čuvanje…") : t("admin.users.action.save", "Sačuvaj")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: UserStatus }) {
  const { t } = useTranslation();
  const palette: Record<UserStatus, { bg: string; fg: string }> = {
    ACTIVE: { bg: "rgba(31,138,86,.12)", fg: "var(--ok)" },
    LOCKED: { bg: "rgba(185,122,14,.16)", fg: "var(--warn)" },
    DISABLED: { bg: "rgba(182,53,58,.16)", fg: "var(--err)" },
  };
  const c = palette[status];
  return (
    <span style={{ background: c.bg, color: c.fg, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
      {t(`admin.users.status.${status}`, status)}
    </span>
  );
}

function Field({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, ...style }}>
      <span style={{ color: "var(--ink-3)" }}>{label}</span>
      {children}
    </label>
  );
}

const table: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
  background: "var(--surface)",
  borderRadius: 8,
  overflow: "hidden",
  boxShadow: "0 1px 2px rgba(0,0,0,.04)",
};
const th: React.CSSProperties = { textAlign: "left", padding: "10px 12px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--surface-2)" };
const chip: React.CSSProperties = { background: "rgba(31,111,178,.12)", color: "var(--info)", fontSize: 11, padding: "2px 8px", borderRadius: 999, fontWeight: 600 };
const input: React.CSSProperties = { padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 14 };
const primary: React.CSSProperties = { padding: "8px 14px", background: "var(--brand-primary)", color: "#fff", border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const secondary: React.CSSProperties = { padding: "8px 14px", background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const linkBtn: React.CSSProperties = { background: "transparent", border: "none", color: "var(--brand-primary)", cursor: "pointer", marginRight: 8, fontSize: 13 };
const dangerLink: React.CSSProperties = { background: "transparent", border: "none", color: "var(--err)", cursor: "pointer", fontSize: 13 };
const panelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: 16,
  background: "rgba(185,122,14,.12)",
  border: "1px solid rgba(185,122,14,.22)",
  borderRadius: 10,
};
const panelTitleStyle: React.CSSProperties = { margin: 0, fontSize: 14, color: "var(--warn)", fontWeight: 600 };
const errorBox: React.CSSProperties = { padding: 10, background: "rgba(182,53,58,.12)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(15,23,42,.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 };
const dialog: React.CSSProperties = { width: "100%", maxWidth: 520, background: "var(--surface)", padding: 24, borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,.18)", display: "flex", flexDirection: "column", gap: 14 };
